import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { Search, MoreVertical } from "lucide-react";
import PageHeader from "../Ui_Components/PageHeader";
import Pagination from "../Ui_Components/Pagination";
import useCustomScrollbar from "../styles/useCustomScrollbar";
import { BASE_URL } from "../api/url";
import {
  approveSuggestion,
  rejectSuggestion,
  getGlobalTemplateList,
  getSuggestedPlanGlobalTemplate,
  getRejectedSuggestions,
} from "../../services/globalTemplates";
import { getMealPlanTypeList } from "../../services/mealPlans";
import { getAvailableNutritionistsList } from "../../services/sales";
import { ToastContainer, useToast } from "../Ui_Components/ToastMessage";
import { useAuth } from "../../context/auth/AuthContext";
import PermissionGate from "../Auth/PermissionGate";
import { Button } from "../Ui_Components/Button";

// ── Types ────────────────────────────────────────────────────────────────────
interface TemplateIngredient {
  name: string;
  quantity: number | string;
  unit: string;
}
interface TemplateMealOption {
  name: string;
  ingredients: TemplateIngredient[];
  nutrientCount: number;
}
interface TemplateMealSlot {
  mealType: string;
  timeStr: string;
  hour: string;
  minute: string;
  period: string;
  options: TemplateMealOption[];
}
interface MealTemplate {
  id: string;
  templateId: number;
  templateCode: string;
  templateName: string;
  goal: string;
  planType: string;
  status: "active" | "draft" | "inactive" | string;
  matchPercent: number;
  mealsPerDay: number;
  optionsPerMeal: number;
  creatorName: string;
  creatorInitial: string;
  creatorColor: string;
  creatorPhoto?: string;
  cal: { min: number; max: number };
  adjProtein: { min: number; max: number };
  carbs: { min: number; max: number };
  fats: { min: number; max: number };
  gl: { min: number; max: number };
  mealSlots: TemplateMealSlot[];
}
interface SuggestedTemplate {
  suggestionId: number;
  planId: number;
  planCode: string;
  mealPlanName: string;
  mealPlanType: string;
  planGoal: string;
  suggestedByNutritionistId: number;
  submittedByName: string;
  submittedByRole: string;
  submittedByPhoto: string;
  suggestedOn: string;
  suggestedTemplateName: string;
  categoryName: string;
  status: "pending" | "approved" | "declined";
  declineReason: string | null;
  reviewedOn: string | null;
}
interface RejectedTemplate {
  suggestionId: number;
  planId: number;
  planCode: string;
  mealPlanName: string;
  mealPlanType: string;
  planGoal: string;
  suggestedTemplateName: string;
  categoryName: string;
  suggestionNote: string;
  submittedByName: string;
  submittedByRole: string;
  submittedByPhoto: string;
  suggestedOn: string;
  reviewedOn: string;
  declineReason: string;
  reviewedByName: string;
}
type TabType = "global" | "suggested" | "rejected";


// ── FilterDropdown ────────────────────────────────────────────────────────────
interface FilterDropdownProps {
  value: string | string[];
  placeholder: string;
  options: string[];
  onChange: (val: string | string[]) => void;
  multiSelect?: boolean;
}
const FilterDropdown: React.FC<FilterDropdownProps> = ({ value, placeholder, options, onChange, multiSelect = false }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; width: number; maxHeight: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  const selectedValues: string[] = multiSelect
    ? (value as string[])
    : (value as string) ? [(value as string)] : [];
  const hasSelection = selectedValues.length > 0;

  useEffect(() => {
    if (open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - r.bottom - 10;
      const spaceAbove = r.top - 10;
      const maxH = 240;
      let top: number;
      let maxHeight: number;
      if (spaceBelow >= maxH || spaceBelow >= spaceAbove) {
        top = r.bottom + 4;
        maxHeight = Math.min(maxH, spaceBelow);
      } else {
        maxHeight = Math.min(maxH, spaceAbove);
        top = r.top - maxHeight - 4;
      }
      setPos({ top, left: r.left, width: r.width, maxHeight });
    }
  }, [open]);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node) && btnRef.current && !btnRef.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  useEffect(() => {
    const h = (e: Event) => {
      if (dropRef.current?.contains(e.target as Node)) return;
      if (open) setOpen(false);
    };
    if (open) window.addEventListener("scroll", h, true);
    return () => window.removeEventListener("scroll", h, true);
  }, [open]);

  const handleOptionClick = (opt: string) => {
    if (multiSelect) {
      const vals = value as string[];
      onChange(vals.includes(opt) ? vals.filter((v) => v !== opt) : [...vals, opt]);
    } else {
      onChange((value as string) === opt ? "" : opt);
      setOpen(false);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(multiSelect ? [] : "");
    setOpen(false);
  };

  const allSelected = multiSelect && options.length > 0 &&
    (value as string[]).length === options.length &&
    options.every((o) => (value as string[]).includes(o));

  const displayText = (() => {
    if (!hasSelection) return "";
    if (!multiSelect) return value as string;
    if (allSelected) return "All";
    const vals = value as string[];
    return vals.length > 0 ? vals[0] : "";
  })();

  return (
    <div className="relative w-full">
      <button ref={btnRef} type="button" onClick={() => setOpen((p) => !p)}
        className={`w-full px-3 py-1 border-[0.5px] rounded-full text-xs font-inter bg-[#F9F9F9] flex items-center gap-1 focus:outline-none transition-colors ${hasSelection ? "border-[#0DB5B5]" : "border-[#D9D9D9]"}`}>
        <span className={`truncate flex-1 text-left text-xs ${displayText ? "text-[#202020]" : "text-[#9E9E9E]"}`}>{displayText || placeholder}</span>
        {multiSelect && !allSelected && selectedValues.length > 1 && (
          <span className="flex-shrink-0 text-xs font-semibold text-[#0DB5B5]">+{selectedValues.length - 1}</span>
        )}
        {hasSelection ? (
          <span onClick={handleClear} className="flex-shrink-0 text-[#9E9E9E] hover:text-[#FF3B30] transition-colors">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </span>
        ) : (
          <svg className={`w-4 h-4 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} fill="none" stroke="#9E9E9E" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
        )}
      </button>
      {open && pos && ReactDOM.createPortal(
        <div ref={dropRef} style={{ position: "fixed", top: `${pos.top}px`, left: `${pos.left}px`, minWidth: `${pos.width}px`, width: "max-content", maxHeight: `${pos.maxHeight}px`, zIndex: 9999 }}
          className="bg-white border border-[#E0E0E0] rounded-md shadow-lg overflow-y-auto custom-scrollbar-overlay"
          onScroll={(e) => e.stopPropagation()}>
          <div className="py-2">
            {options.map((opt) => {
              const isSelected = !allSelected && selectedValues.includes(opt);
              return (
                <div key={opt} onClick={() => handleOptionClick(opt)}
                  className={`px-3 py-2 mx-2 my-1 text-xs font-inter cursor-pointer rounded-md flex items-center gap-2 ${isSelected ? "bg-[#E6FBFB] text-[#0DB5B5]" : "text-[#202020] hover:bg-[#F5F5F5]"}`}>
                  <span>{opt}</span>
                </div>
              );
            })}
          </div>
        </div>, document.body
      )}
    </div>
  );
};

// ── PlanCard (no matchPercent badge) ─────────────────────────────────────────
const PlanCard: React.FC<{ plan: MealTemplate; onPreview: () => void; onUse: () => void }> = ({ plan, onPreview }) => (
  <div
    onClick={onPreview}
    className="bg-white rounded-lg p-4 border border-[#E0E0E0] flex flex-col gap-2.5 cursor-pointer hover:border-[#0DB5B5] hover:shadow-sm transition-all"
  >
    <div className="flex flex-col gap-0.5">
      <div className="flex items-start justify-between gap-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-sm font-semibold text-[#1F1F1F]">{plan.templateName || plan.templateCode}</span>
          <span className="text-[#D9D9D9] text-sm select-none">|</span>
          <span className={`text-sm font-medium ${plan.matchPercent >= 90 ? "text-[#0DB5B5]" : plan.matchPercent >= 75 ? "text-[#FF9F0A]" : "text-[#8E8E93]"}`}>{plan.planType}</span>
        </div>
        {plan.status && (
          <span className={`flex-shrink-0 px-2 py-0.5 rounded-[24px] text-[11px] font-medium ${
            plan.status === "active" ? "bg-[#E5F8F8] text-[#0DB5B5]" : "bg-[#F0F0F0] text-[#646464]"
          }`}>
            {plan.status.charAt(0).toUpperCase() + plan.status.slice(1)}
          </span>
        )}
      </div>
      <p className="text-xs text-[#8E8E93]">{plan.mealsPerDay} meals/day · {plan.optionsPerMeal} options/meal</p>
    </div>
    <div className="flex items-center gap-1.5">
      {plan.creatorPhoto ? (
        <img
          src={`${BASE_URL}${plan.creatorPhoto}`}
          alt={plan.creatorName}
          className="w-5 h-5 rounded-full object-cover flex-shrink-0"
          onError={(e) => {
            const t = e.currentTarget;
            t.style.display = "none";
            const fb = t.nextElementSibling as HTMLElement | null;
            if (fb) fb.style.display = "flex";
          }}
        />
      ) : null}
      <div
        className="w-5 h-5 rounded-full items-center justify-center text-white text-[10px] font-semibold flex-shrink-0"
        style={{ backgroundColor: plan.creatorColor, display: plan.creatorPhoto ? "none" : "flex" }}
      >
        {plan.creatorInitial}
      </div>
      <span className="text-xs text-[#646464]">{plan.creatorName}</span>
    </div>
    <div className="border-t border-[#F0F0F0]" />
    <div className="grid grid-cols-3 gap-x-2 gap-y-1 text-xs">
      <span className="text-[#8E8E93]">Cal - <span className="text-[#FF6B6B] font-medium">{plan.cal.min}-{plan.cal.max}</span></span>
      <span className="text-[#8E8E93]">A.pro - <span className="text-[#4ECDC4] font-medium">{plan.adjProtein.min}-{plan.adjProtein.max}g</span></span>
      <span className="text-[#8E8E93]">Carb - <span className="text-[#FFD93D] font-medium">{plan.carbs.min}-{plan.carbs.max}g</span></span>
      <span className="text-[#8E8E93]">Fats - <span className="text-[#C084FC] font-medium">{plan.fats.min}-{plan.fats.max}g</span></span>
      <span className="text-[#8E8E93]">GL - <span className="text-[#84CC16] font-medium">{String(plan.gl.min).padStart(2, "0")}-{plan.gl.max}</span></span>
    </div>
    {/* <div className="flex items-center gap-2 pt-1">
      <button onClick={onPreview} className="flex-1 px-3 py-1.5 border border-[#E0E0E0] rounded-md text-xs font-medium text-[#1F1F1F] hover:border-[#0DB5B5] transition-colors">Preview</button>
      <button onClick={onUse} className="flex-1 px-3 py-1.5 bg-[#0DB5B5] text-white rounded-md text-xs font-medium hover:bg-[#0CA0A0] transition-colors">Duplicate &amp; use</button>
    </div> */}
  </div>
);

// ── PreviewMealColumn ─────────────────────────────────────────────────────────


// ── ActionMenu (3-dot) ────────────────────────────────────────────────────────
const ActionMenu: React.FC<{ items: Array<{ label: string; onClick: () => void; danger?: boolean }> }> = ({ items }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.right - 160 });
    }
  }, [open]);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node) && btnRef.current && !btnRef.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  return (
    <div className="relative">
      <button ref={btnRef} onClick={(e) => { e.stopPropagation(); setOpen((p) => !p); }} className="p-1 hover:bg-gray-100 rounded transition-colors inline-flex">
        <MoreVertical className="w-5 h-5 text-[#646464]" />
      </button>
      {open && pos && ReactDOM.createPortal(
        <div ref={menuRef} style={{ position: "fixed", top: pos.top, left: pos.left, minWidth: 160, zIndex: 9999, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
          className="bg-white border border-[#E0E0E0] rounded-lg overflow-hidden">
          {items.map((item, index) => (
            <React.Fragment key={item.label}>
              {index > 0 && <div className="h-px bg-[#F3F4F6]" />}
              <button
                onClick={(e) => { e.stopPropagation(); item.onClick(); setOpen(false); }}
                className={`w-full text-left px-4 text-sm font-inter transition-colors ${item.danger ? "text-[#FF3B30] hover:bg-[#FFF5F5]" : "text-[#1F1F1F] hover:bg-[#F9FEFE]"}`}
                style={{ padding: "10px 16px" }}
              >
                {item.label}
              </button>
            </React.Fragment>
          ))}
        </div>, document.body
      )}
    </div>
  );
};

// ── ReasonCell with tooltip ───────────────────────────────────────────────────
const ReasonCell: React.FC<{ reason: string; containerRef?: React.RefObject<HTMLDivElement | null> }> = ({ reason, containerRef }) => {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; maxWidth: number } | null>(null);
  const ref = useRef<HTMLSpanElement>(null);
  return (
    <>
      <span ref={ref} className="text-sm sm:text-base font-inter text-[#1F1F1F] cursor-default whitespace-nowrap"
        onMouseEnter={() => {
          if (ref.current) {
            const r = ref.current.getBoundingClientRect();
            const containerRight = containerRef?.current?.getBoundingClientRect().right ?? window.innerWidth;
            setPos({ top: r.bottom + 6, left: r.left, maxWidth: containerRight - r.left - 8 });
          }
          setShow(true);
        }}
        onMouseLeave={() => setShow(false)}>
        {reason.length > 20 ? `${reason.slice(0, 20)}...` : reason}
      </span>
      {show && pos && ReactDOM.createPortal(
        <div style={{ position: "fixed", top: pos.top, left: pos.left, maxWidth: pos.maxWidth, zIndex: 9999 }} className="bg-[#1F1F1F] text-white text-xs rounded-md px-3 py-2 shadow-lg leading-relaxed">
          {reason}
        </div>, document.body
      )}
    </>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const GlobalTemplatePage: React.FC = () => {
  useCustomScrollbar();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { user } = useAuth();
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const [activeTab, setActiveTab] = useState<TabType>(
    ((location.state as { returnTab?: TabType } | null)?.returnTab) || "global"
  );

  // Reject modal state
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectingSuggestionId, setRejectingSuggestionId] = useState<number | null>(null);
  const [isSubmittingReject, setIsSubmittingReject] = useState(false);

  // Approve confirmation state
  const [isApproveConfirmOpen, setIsApproveConfirmOpen] = useState(false);
  const [approvingSuggestionId, setApprovingSuggestionId] = useState<number | null>(null);
  const [isSubmittingApprove, setIsSubmittingApprove] = useState(false);

  const openApproveModal = (suggestionId: number) => {
    setApprovingSuggestionId(suggestionId);
    setIsApproveConfirmOpen(true);
  };

  const handleConfirmApprove = async () => {
    if (!approvingSuggestionId) return;
    setIsSubmittingApprove(true);
    try {
      const data = await approveSuggestion(approvingSuggestionId, { nutritionist_id: user?.nutrist_id });
      if (data.success === true || data.success === "1" || data.success === 1) {
        setIsApproveConfirmOpen(false);
        fetchSuggestedTemplates();
        toast.success("Approved!", data.data?.message || "Template approved successfully.");
      } else {
        toast.error("Failed", data.message || "Could not approve template.");
      }
    } catch {
      toast.error("Error", "Failed to approve template.");
    } finally {
      setIsSubmittingApprove(false);
    }
  };

  const openRejectModal = (suggestionId: number) => {
    setRejectingSuggestionId(suggestionId);
    setRejectReason("");
    setIsRejectModalOpen(true);
  };

  const handleSubmitReject = async () => {
    if (!rejectReason.trim() || !rejectingSuggestionId) return;
    setIsSubmittingReject(true);
    try {
      const data = await rejectSuggestion(rejectingSuggestionId, {
        nutritionist_id: user?.nutrist_id,
        decline_reason: rejectReason.trim(),
      });
      if (data.success === true || data.success === "1" || data.success === 1) {
        setIsRejectModalOpen(false);
        setRejectReason("");
        fetchSuggestedTemplates();
        setActiveTab("rejected");
        toast.success("Submitted successfully!", data.data?.message || "Template has been rejected.");
      } else {
        toast.error("Failed", data.message || "Could not reject template.");
      }
    } catch {
      toast.error("Error", "Failed to submit rejection.");
    } finally {
      setIsSubmittingReject(false);
    }
  };

  // Global template list — fetched from API
  const [globalTemplates, setGlobalTemplates] = useState<MealTemplate[]>([]);
  const [globalTemplatesLoading, setGlobalTemplatesLoading] = useState(false);
  const [globalTemplatesError, setGlobalTemplatesError] = useState<string | null>(null);
  const [globalTotal, setGlobalTotal] = useState(0);
  const [globalPage, setGlobalPage] = useState(1);

  // Plan type list fetched once from API
  const [planTypesList, setPlanTypesList] = useState<string[]>([]);

  // Global template tab
  const [planTypeFilter, setPlanTypeFilter] = useState("");
  const [optionsFilter, setOptionsFilter] = useState("");
  const [mealCountFilter, setMealCountFilter] = useState("");
  const [templateSearch, setTemplateSearch] = useState("");
  const [debouncedTemplateSearch, setDebouncedTemplateSearch] = useState("");
  const [nutritionistFilter, setNutritionistFilter] = useState<string[]>([]);
  const [nutritionistsList, setNutritionistsList] = useState<{ id: string; name: string }[]>([]);

  // Suggested tab
  const [suggestedSearch, setSuggestedSearch] = useState("");
  const [debouncedSuggestedSearch, setDebouncedSuggestedSearch] = useState("");
  const [suggestedPage, setSuggestedPage] = useState(1);
  const [suggestedTemplates, setSuggestedTemplates] = useState<SuggestedTemplate[]>([]);
  const [suggestedLoading, setSuggestedLoading] = useState(false);
  const [suggestedError, setSuggestedError] = useState<string | null>(null);
  const [suggestedTotal, setSuggestedTotal] = useState(0);
  const [suggestedCounts, setSuggestedCounts] = useState({ pending: 0, approved: 0, declined: 0 });

  // Rejected tab
  const [rejectedSearch, setRejectedSearch] = useState("");
  const [debouncedRejectedSearch, setDebouncedRejectedSearch] = useState("");
  const [rejectedPage, setRejectedPage] = useState(1);
  const [rejectedTemplates, setRejectedTemplates] = useState<RejectedTemplate[]>([]);
  const [rejectedLoading, setRejectedLoading] = useState(false);
  const [rejectedError, setRejectedError] = useState<string | null>(null);
  const [rejectedTotal, setRejectedTotal] = useState(0);
  const [rejectedCounts, setRejectedCounts] = useState({ pending: 0, approved: 0, declined: 0 });

  const globalItemsPerPage = 12;
  const itemsPerPage = 10;

  const filteredTemplates = globalTemplates;

  const globalTotalPages = Math.max(1, Math.ceil(globalTotal / globalItemsPerPage));
  const suggestedTotalPages = Math.max(1, Math.ceil(suggestedTotal / itemsPerPage));
  const pagedSuggested = suggestedTemplates;

  const rejectedTotalPages = Math.max(1, Math.ceil(rejectedTotal / itemsPerPage));
  const pagedRejected = rejectedTemplates;

  const optionsPerMealList = ["1", "2", "3", "4", "5"];
  const mealsPerDayList = Array.from({ length: 13 }, (_, i) => String(i + 1));

  // Fetch nutritionists once on mount; pre-select all
  useEffect(() => {
    getAvailableNutritionistsList()
      .then((data) => {
        if (data.success && Array.isArray(data.data)) {
          const list = (data.data as Array<{ nutrist_id: number; nutrist_fname?: string; nutrist_lname?: string }>).map((n) => ({
            id: String(n.nutrist_id),
            name: `${n.nutrist_fname ?? ""} ${n.nutrist_lname ?? ""}`.trim(),
          }));
          setNutritionistsList(list);
          setNutritionistFilter(list.map((n) => n.name));
        }
      })
      .catch(() => {});
  }, []);

  // Fetch plan type list once on mount
  useEffect(() => {
    getMealPlanTypeList()
      .then((data) => {
        if (data.success === "1" && Array.isArray(data.meal_plan_data)) {
          setPlanTypesList(data.meal_plan_data.map((item: { meal_plan_type_name: string }) => item.meal_plan_type_name.trim()).filter(Boolean));
        }
      })
      .catch(() => {});
  }, []);

  // Debounce global template search — reset page and update debounced value after 400ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTemplateSearch(templateSearch);
      setGlobalPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [templateSearch]);

  // Reset page when filters change
  useEffect(() => { setGlobalPage(1); }, [planTypeFilter, optionsFilter, mealCountFilter, nutritionistFilter]);

  // Fetch global template list from API
  useEffect(() => {
    const fetchGlobalTemplates = async () => {
      setGlobalTemplatesLoading(true);
      setGlobalTemplatesError(null);
      try {
        const nutIds = nutritionistFilter.length > 0 && nutritionistsList.length > 0 && nutritionistFilter.length < nutritionistsList.length
          ? nutritionistsList.filter((n) => nutritionistFilter.includes(n.name)).map((n) => parseInt(n.id, 10))
          : [];
        const api = await getGlobalTemplateList(
          globalPage, globalItemsPerPage,
          debouncedTemplateSearch,
          planTypeFilter,
          mealCountFilter,
          optionsFilter,
          nutIds,
        );
        if (api.success === true || api.success === "1" || api.success === "true") {
          const results = api.data?.results ?? api.data ?? [];
          setGlobalTotal(api.data?.total ?? results.length);
          const mapped: MealTemplate[] = (results as any[]).map((t: any) => ({
            id:             String(t.template_id),
            templateId:     t.template_id,
            templateCode:   t.template_code || "",
            templateName:   t.template_name || t.template_code || "",
            goal:           t.plan_goal || t.category_name || "",
            planType:       t.meal_plan_type || "",
            status:         (t.template_status || "draft").toLowerCase(),
            matchPercent:   0,
            mealsPerDay:    t.meals_per_day ?? 0,
            optionsPerMeal: t.avg_options_per_meal ?? 0,
            creatorName:    t.created_by_name || "",
            creatorInitial: (t.created_by_name || "?").charAt(0).toUpperCase(),
            creatorColor:   "#0DB5B5",
            creatorPhoto:   t.nutritionist_profile || "",
            cal:            { min: parseFloat(t.total_calories_min) || 0, max: parseFloat(t.total_calories_max) || 0 },
            adjProtein:     { min: parseFloat(t.total_protein_min) || 0,  max: parseFloat(t.total_protein_max) || 0 },
            carbs:          { min: parseFloat(t.total_carbs_min) || 0,    max: parseFloat(t.total_carbs_max) || 0 },
            fats:           { min: parseFloat(t.total_fats_min) || 0,     max: parseFloat(t.total_fats_max) || 0 },
            gl:             { min: parseFloat(t.total_GL_min) || 0,       max: parseFloat(t.total_GL_max) || 0 },
            mealSlots:      [],
          }));
          setGlobalTemplates(mapped);
        } else {
          setGlobalTemplatesError(api.message || "Failed to load templates");
        }
      } catch (err) {
        setGlobalTemplatesError(err instanceof Error ? err.message : "Failed to load templates");
      } finally {
        setGlobalTemplatesLoading(false);
      }
    };
    fetchGlobalTemplates();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalPage, debouncedTemplateSearch, planTypeFilter, mealCountFilter, optionsFilter, nutritionistFilter, nutritionistsList]);

  // Debounce suggested search — reset page and update debounced value after 400ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSuggestedSearch(suggestedSearch);
      setSuggestedPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [suggestedSearch]);

  // Debounce rejected search — reset page and update debounced value after 400ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedRejectedSearch(rejectedSearch);
      setRejectedPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [rejectedSearch]);

  useEffect(() => { setSuggestedSearch(""); setRejectedSearch(""); setTemplateSearch(""); }, [activeTab]);

  const fetchSuggestedTemplates = async () => {
    setSuggestedLoading(true);
    setSuggestedError(null);
    try {
      const api = await getSuggestedPlanGlobalTemplate("pending", suggestedPage, 10, debouncedSuggestedSearch);
      if (api.success === true || api.success === "1" || api.success === "true") {
        const d = api.data;
        setSuggestedCounts(d.counts ?? { pending: 0, approved: 0, declined: 0 });
        setSuggestedTotal(d.total ?? 0);
        const mapped: SuggestedTemplate[] = (d.results ?? []).map((t: any) => ({
          suggestionId: t.suggestion_id,
          planId:       t.plan_id,
          planCode:     t.plan_code || "",
          mealPlanName: t.meal_plan_name || t.plan_code || "",
          mealPlanType: t.meal_plan_type || "",
          planGoal:     t.plan_goal || "",
          suggestedByNutritionistId: t.suggested_by_nutritionist_id ?? 0,
          submittedByName: t.submitted_by_name || "",
          submittedByRole: t.submitted_by_role || "",
          submittedByPhoto: t.submitted_by_photo || "",
          suggestedOn:  t.suggested_on ? new Date(t.suggested_on).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "",
          suggestedTemplateName: t.suggested_template_name || "",
          categoryName: t.category_name || "",
          status:       t.status || "pending",
          declineReason: t.decline_reason || null,
          reviewedOn:   t.reviewed_on || null,
        }));
        setSuggestedTemplates(mapped);
      } else {
        setSuggestedError(api.message || "Failed to load suggested templates");
      }
    } catch (err) {
      setSuggestedError(err instanceof Error ? err.message : "Failed to load suggested templates");
    } finally {
      setSuggestedLoading(false);
    }
  };

  useEffect(() => {
    fetchSuggestedTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedPage, debouncedSuggestedSearch]);

  const fetchRejectedTemplates = async () => {
    setRejectedLoading(true);
    setRejectedError(null);
    try {
      const api = await getRejectedSuggestions(rejectedPage, 10, debouncedRejectedSearch);
      if (api.success === true || api.success === "1" || api.success === "true") {
        const d = api.data;
        setRejectedTotal(d.total ?? 0);
        setRejectedCounts(d.counts ?? { pending: 0, approved: 0, declined: 0 });
        const mapped: RejectedTemplate[] = (d.results ?? []).map((t: any) => ({
          suggestionId:          t.suggestion_id,
          planId:                t.plan_id,
          planCode:              t.plan_code || "",
          mealPlanName:          t.meal_plan_name || t.plan_code || "",
          mealPlanType:          t.meal_plan_type || "",
          planGoal:              t.plan_goal || "",
          suggestedTemplateName: t.suggested_template_name || "",
          categoryName:          t.category_name || "",
          suggestionNote:        t.suggestion_note || "",
          submittedByName:       t.submitted_by_name || "",
          submittedByRole:       t.submitted_by_role || "",
          submittedByPhoto:      t.submitted_by_photo || "",
          suggestedOn:           t.suggested_on ? new Date(t.suggested_on).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "",
          reviewedOn:            t.reviewed_on ? new Date(t.reviewed_on).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "",
          declineReason:         t.decline_reason || "",
          reviewedByName:        t.reviewed_by_name || "",
        }));
        setRejectedTemplates(mapped);
      } else {
        setRejectedError(api.message || "Failed to load rejected templates");
      }
    } catch (err) {
      setRejectedError(err instanceof Error ? err.message : "Failed to load rejected templates");
    } finally {
      setRejectedLoading(false);
    }
  };

  useEffect(() => {
    fetchRejectedTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rejectedPage, debouncedRejectedSearch]);

  return (
    <div className="h-screen flex flex-col bg-[#F7F7FA]">
      <PageHeader title="Global template" />

      <div className="px-4 sm:px-6 lg:px-8 pb-4 flex-1 flex flex-col overflow-hidden">
        {/* ── Tabs ── */}
        <div className="flex gap-4 mb-4 border-b border-gray-200 flex-shrink-0">
          {/* Global template tab */}
          <button
            onClick={() => setActiveTab("global")}
            className={`pb-3 px-1 relative text-sm sm:text-base font-medium transition-colors ${activeTab === "global" ? "text-[#1F1F1F]" : "text-gray-500 hover:text-gray-700"}`}
          >
            Global template{" "}
            <span className={`ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-semibold ${activeTab === "global" ? "bg-[#00A9A9] text-white" : "bg-gray-400 text-white"}`}>
              {globalTotal}
            </span>
            {activeTab === "global" && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#00A9A9]" />}
          </button>

          {/* Suggested template tab */}
          <button
            onClick={() => setActiveTab("suggested")}
            className={`pb-3 px-1 relative text-sm sm:text-base font-medium transition-colors ${activeTab === "suggested" ? "text-[#00A9A9]" : "text-gray-500 hover:text-gray-700"}`}
          >
            Suggested template{" "}
            <span className={`ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-semibold ${activeTab === "suggested" ? "bg-[#00A9A9] text-white" : "bg-gray-400 text-white"}`}>
              {suggestedCounts.pending}
            </span>
            {activeTab === "suggested" && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#00A9A9]" />}
          </button>

          {/* Rejected template tab */}
          <button
            onClick={() => setActiveTab("rejected")}
            className={`pb-3 px-1 relative text-sm sm:text-base font-medium transition-colors ${activeTab === "rejected" ? "text-[#1F1F1F]" : "text-gray-500 hover:text-gray-700"}`}
          >
            Rejected template{" "}
            <span className={`ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-semibold ${activeTab === "rejected" ? "bg-[#00A9A9] text-white" : "bg-gray-400 text-white"}`}>
              {rejectedCounts.declined}
            </span>
            {activeTab === "rejected" && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#00A9A9]" />}
          </button>
        </div>

        {/* ── Table / Card Container ── */}
        <div ref={tableContainerRef} className="bg-white rounded-[6px] shadow-sm flex-1 flex flex-col border-[0.5px] border-[#F3F4F6] overflow-hidden">

          {/* ═══════ Global template ═══════ */}
          {activeTab === "global" && (
            <>
              {/* Header */}
              <div className="p-4 sm:px-6 sm:py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 flex-shrink-0">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
                  Browse meal plans ({filteredTemplates.length})
                </h3>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="relative">
                    <input type="text" placeholder="Search template.." value={templateSearch} onChange={(e) => setTemplateSearch(e.target.value)}
                      className="w-full sm:w-auto pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-400 text-sm" />
                    <Search className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  </div>
                  <PermissionGate resource="global_template" action="create">
                    <Button
                      variant="primary"
                      onClick={() => navigate("/global-template/create", { state: { globalTemplateMode: true } })}
                      startIcon={<svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>}
                    >
                      Create
                    </Button>
                  </PermissionGate>
                </div>
              </div>

              {/* Filters row */}
              <div className="px-5 sm:px-6 pb-3 flex items-center gap-2 flex-wrap flex-shrink-0 border-b border-[#111C2D1A]">
                <div className="w-[136px]">
                  <FilterDropdown
                    value={nutritionistFilter}
                    placeholder="Nutritionist"
                    options={nutritionistsList.map((n) => n.name)}
                    onChange={(val) => setNutritionistFilter(val as string[])}
                    multiSelect={true}
                  />
                </div>
                <div className="w-[136px]">
                  <FilterDropdown value={planTypeFilter} placeholder="Select plan type" options={planTypesList} onChange={(v) => setPlanTypeFilter(v as string)} />
                </div>
                {/* <div className="w-[136px]">
                  <FilterDropdown value={categoryFilter} placeholder="Select category" options={["Veg", "Non-Veg", "Vegan", "Detox"]} onChange={(v) => setCategoryFilter(v as string)} />
                </div> */}
                <div className="w-[150px]">
                  <FilterDropdown value={optionsFilter} placeholder="Select options" options={optionsPerMealList.map((o) => o + " options/meal")} onChange={(v) => setOptionsFilter((v as string).replace(" options/meal", ""))} />
                </div>
                <div className="w-[150px]">
                  <FilterDropdown value={mealCountFilter} placeholder="Select no. of meal" options={mealsPerDayList.map((o) => o + " meals/day")} onChange={(v) => setMealCountFilter((v as string).replace(" meals/day", ""))} />
                </div>
              </div>

              {/* Cards */}
              <div className="flex-1 overflow-y-auto custom-scrollbar-overlay p-5">
                {globalTemplatesLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-8 h-8 border-2 border-[#0DB5B5] border-t-transparent rounded-full animate-spin" />
                      <p className="text-sm text-[#8E8E93]">Loading templates...</p>
                    </div>
                  </div>
                ) : globalTemplatesError ? (
                  <div className="flex flex-col items-center justify-center h-full gap-3">
                    <p className="text-sm text-red-500">{globalTemplatesError}</p>
                    <Button
                      variant="primary"
                      onClick={() => window.location.reload()}
                    >
                      Retry
                    </Button>
                  </div>
                ) : filteredTemplates.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full gap-3 py-16">
                    <p className="text-base font-semibold text-[#1F1F1F]">No global meal plans yet</p>
                    <p className="text-sm text-gray-400 text-center max-w-[320px] leading-relaxed">
                      Create reusable meal plan templates that can be assigned to multiple clients and customized when needed.
                    </p>
                    <PermissionGate resource="global_template" action="create">
                      <Button
                        variant="primary"
                        size="md"
                        onClick={() => navigate("/global-template/create", { state: { globalTemplateMode: true } })}
                        startIcon={
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                          </svg>
                        }
                        className="mt-1"
                      >
                        Create template
                      </Button>
                    </PermissionGate>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 xl:grid-cols-3 3xl:grid-cols-4 gap-4">
                    {filteredTemplates.map((t) => (
                      <PlanCard
                        key={t.id}
                        plan={t}
                        onPreview={() => navigate(`/global-template/plan/${t.templateId}`, { state: { globalTemplateMode: true, fromGlobal: true, returnTab: "global", templateStatus: t.status } })}
                        onUse={() => navigate(`/global-template/plan/${t.templateId}`, { state: { globalTemplateMode: true, fromGlobal: true, returnTab: "global", templateStatus: t.status } })}
                      />
                    ))}
                  </div>
                )}
              </div>
              {/* Pagination */}
              <Pagination currentPage={globalPage} totalPages={globalTotalPages} onPageChange={setGlobalPage} resultCount={filteredTemplates.length} />
            </>
          )}

          {/* ═══════ Suggested template ═══════ */}
          {activeTab === "suggested" && (
            <>
              {/* Header */}
              <div className="p-4 sm:px-6 sm:py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 flex-shrink-0">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
                  Suggested template ({pagedSuggested.length})
                </h3>
                <div className="relative w-full sm:w-auto">
                  <input type="text" placeholder="Search" value={suggestedSearch} onChange={(e) => setSuggestedSearch(e.target.value)}
                    className="w-full sm:w-auto pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-400 text-sm" />
                  <Search className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                </div>
              </div>

              {/* Table */}
              <div className="flex-1 overflow-hidden flex flex-col">
                <div className="flex-shrink-0 overflow-x-auto bg-white border-b border-[#111C2D33] mx-5 custom-scrollbar-overlay">
                  <table className="w-full min-w-[640px] table-fixed">
                    <thead>
                      <tr className="bg-white">
                        <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[28%]">Submitted by</th>
                        <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[15%]">Meal plan</th>
                        <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[16%]">Plan type</th>
                        <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[20%]">Goal</th>
                        <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[16%]">Requested on</th>
                        {/* <th className="w-[5%]" /> */}
                      </tr>
                    </thead>
                  </table>
                </div>
                <div className="flex-1 overflow-x-auto overflow-y-auto mx-5 custom-scrollbar-overlay">
                  {suggestedLoading ? (
                    <div className="flex items-center justify-center h-full py-16">
                      <div className="w-8 h-8 border-2 border-[#0DB5B5] border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : suggestedError ? (
                    <div className="flex flex-col items-center justify-center h-full py-16 gap-3">
                      <p className="text-sm text-red-500">{suggestedError}</p>
                      <Button
                        variant="primary"
                        onClick={fetchSuggestedTemplates}
                      >
                        Retry
                      </Button>
                    </div>
                  ) : pagedSuggested.length === 0 ? (
                    <div className="flex items-center justify-center h-full py-16">
                      <p className="text-gray-400 text-sm sm:text-base font-inter">No suggested templates found.</p>
                    </div>
                  ) : (
                    <table className="w-full min-w-[640px] table-fixed">
                      <tbody>
                        {pagedSuggested.map((item, index) => {
                          const goToDetail = () => navigate(
                            `/global-template/suggestion/${item.suggestionId}`,
                            {
                              state: {
                                approvalMode: true,
                                fromGlobal: true,
                                returnTab: "suggested",
                                submitterInfo: {
                                  name: item.submittedByName,
                                  role: item.submittedByRole,
                                  goal: item.planGoal,
                                  avatarInitial: item.submittedByName.charAt(0).toUpperCase(),
                                  avatarColor: "#34C759",
                                  photo: item.submittedByPhoto || "",
                                },
                              }
                            }
                          );
                          return (
                          <tr key={item.suggestionId} onClick={goToDetail} className="hover:bg-gray-50 transition-colors cursor-pointer">
                            <td className={`py-3 sm:py-4 px-4 sm:px-6 w-[28%] ${index !== pagedSuggested.length - 1 ? "border-b border-[#111C2D1A]" : ""}`}>
                              <div className="flex items-center gap-2 sm:gap-3">
                                {item.submittedByPhoto ? (
                                  <img
                                    src={`${BASE_URL}${item.submittedByPhoto}`}
                                    alt={item.submittedByName}
                                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover flex-shrink-0"
                                    onError={(e) => {
                                      const target = e.currentTarget;
                                      target.style.display = "none";
                                      const fallback = target.nextElementSibling as HTMLElement | null;
                                      if (fallback) fallback.style.display = "flex";
                                    }}
                                  />
                                ) : null}
                                <div
                                  className="w-8 h-8 sm:w-10 sm:h-10 rounded-full items-center justify-center text-white text-sm font-semibold flex-shrink-0"
                                  style={{ backgroundColor: "#34C759", display: item.submittedByPhoto ? "none" : "flex" }}
                                >
                                  {item.submittedByName.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <div className="font-medium font-inter text-[#1F1F1F] text-sm sm:text-base">{item.submittedByName}</div>
                                  <div className="text-xs sm:text-sm font-inter text-gray-500">{item.submittedByRole}</div>
                                </div>
                              </div>
                            </td>
                            <td className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base font-inter w-[15%] ${index !== pagedSuggested.length - 1 ? "border-b border-[#111C2D1A]" : ""}`}>{item.mealPlanName}</td>
                            <td className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base font-inter w-[16%] ${index !== pagedSuggested.length - 1 ? "border-b border-[#111C2D1A]" : ""}`}>{item.mealPlanType}</td>
                            <td className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base font-inter w-[20%] ${index !== pagedSuggested.length - 1 ? "border-b border-[#111C2D1A]" : ""}`}>{item.planGoal}</td>
                            <td className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base font-inter w-[16%] ${index !== pagedSuggested.length - 1 ? "border-b border-[#111C2D1A]" : ""}`}>{item.suggestedOn}</td>
                            {/* <td onClick={(e) => e.stopPropagation()} className={`py-3 sm:py-4 px-2 w-[5%] ${index !== pagedSuggested.length - 1 ? "border-b border-[#111C2D1A]" : ""}`}>
                              <ActionMenu items={[{ label: "View", onClick: goToDetail }, { label: "Approve", onClick: () => openApproveModal(item.suggestionId) }, { label: "Reject", onClick: () => openRejectModal(item.suggestionId), danger: true }]} />
                            </td> */}
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
              <Pagination currentPage={suggestedPage} totalPages={suggestedTotalPages} onPageChange={setSuggestedPage} resultCount={pagedSuggested.length} />
            </>
          )}

          {/* ═══════ Rejected template ═══════ */}
          {activeTab === "rejected" && (
            <>
              {/* Header */}
              <div className="p-4 sm:px-6 sm:py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 flex-shrink-0">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
                  Rejected template ({pagedRejected.length})
                </h3>
                <div className="relative w-full sm:w-auto">
                  <input type="text" placeholder="Search" value={rejectedSearch} onChange={(e) => setRejectedSearch(e.target.value)}
                    className="w-full sm:w-auto pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-400 text-sm" />
                  <Search className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                </div>
              </div>

              {/* Table */}
              <div className="flex-1 overflow-hidden flex flex-col">
                <div className="flex-shrink-0 overflow-x-auto bg-white border-b border-[#111C2D33] mx-5 custom-scrollbar-overlay">
                  <table className="w-full min-w-[640px] table-fixed">
                    <thead>
                      <tr className="bg-white">
                        <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[28%]">Submitted by</th>
                        <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[15%]">Meal plan</th>
                        <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[16%]">Plan type</th>
                        <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[20%]">Goal</th>
                        <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[16%]">Reason</th>
                        <th className="w-[5%]" />
                      </tr>
                    </thead>
                  </table>
                </div>
                <div className="flex-1 overflow-x-auto overflow-y-auto mx-5 custom-scrollbar-overlay">
                  {rejectedLoading ? (
                    <div className="flex items-center justify-center h-full py-16">
                      <div className="w-8 h-8 border-2 border-[#0DB5B5] border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : rejectedError ? (
                    <div className="flex flex-col items-center justify-center h-full py-16 gap-3">
                      <p className="text-sm text-red-500">{rejectedError}</p>
                      <Button
                        variant="primary"
                        onClick={fetchRejectedTemplates}
                      >
                        Retry
                      </Button>
                    </div>
                  ) : pagedRejected.length === 0 ? (
                    <div className="flex items-center justify-center h-full py-16">
                      <p className="text-gray-400 text-sm sm:text-base font-inter">No rejected templates found.</p>
                    </div>
                  ) : (
                    <table className="w-full min-w-[640px] table-fixed">
                      <tbody>
                        {pagedRejected.map((item, index) => {
                          const goToDetail = () => navigate(
                            `/global-template/suggestion/${item.suggestionId}`,
                            {
                              state: {
                                approvalMode: true,
                                fromRejected: true,
                                fromGlobal: true,
                                returnTab: "rejected",
                                submitterInfo: {
                                  name: item.submittedByName,
                                  role: item.submittedByRole,
                                  goal: item.planGoal,
                                  avatarInitial: item.submittedByName.charAt(0).toUpperCase(),
                                  avatarColor: "#34C759",
                                  photo: item.submittedByPhoto || "",
                                },
                                rejectionInfo: {
                                  reason: item.declineReason,
                                  reviewedByName: item.reviewedByName,
                                  reviewedOn: item.reviewedOn,
                                },
                              }
                            }
                          );
                          return (
                          <tr key={item.suggestionId} onClick={goToDetail} className="hover:bg-gray-50 transition-colors cursor-pointer">
                            <td className={`py-3 sm:py-4 px-4 sm:px-6 w-[28%] ${index !== pagedRejected.length - 1 ? "border-b border-[#111C2D1A]" : ""}`}>
                              <div className="flex items-center gap-2 sm:gap-3">
                                {item.submittedByPhoto ? (
                                  <img
                                    src={`${BASE_URL}${item.submittedByPhoto}`}
                                    alt={item.submittedByName}
                                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover flex-shrink-0"
                                    onError={(e) => {
                                      const target = e.currentTarget;
                                      target.style.display = "none";
                                      const fallback = target.nextElementSibling as HTMLElement | null;
                                      if (fallback) fallback.style.display = "flex";
                                    }}
                                  />
                                ) : null}
                                <div
                                  className="w-8 h-8 sm:w-10 sm:h-10 rounded-full items-center justify-center text-white text-sm font-semibold flex-shrink-0"
                                  style={{ backgroundColor: "#34C759", display: item.submittedByPhoto ? "none" : "flex" }}
                                >
                                  {item.submittedByName.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <div className="font-medium font-inter text-[#1F1F1F] text-sm sm:text-base">{item.submittedByName}</div>
                                  <div className="text-xs sm:text-sm font-inter text-gray-500">{item.submittedByRole}</div>
                                </div>
                              </div>
                            </td>
                            <td className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base font-inter w-[15%] ${index !== pagedRejected.length - 1 ? "border-b border-[#111C2D1A]" : ""}`}>{item.mealPlanName}</td>
                            <td className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base font-inter w-[16%] ${index !== pagedRejected.length - 1 ? "border-b border-[#111C2D1A]" : ""}`}>{item.mealPlanType}</td>
                            <td className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base font-inter w-[20%] ${index !== pagedRejected.length - 1 ? "border-b border-[#111C2D1A]" : ""}`}>{item.planGoal}</td>
                            <td className={`py-3 sm:py-4 px-4 sm:px-6 w-[16%] ${index !== pagedRejected.length - 1 ? "border-b border-[#111C2D1A]" : ""}`}>
                              <ReasonCell reason={item.declineReason} containerRef={tableContainerRef} />
                            </td>
                            <td className={`py-3 sm:py-4 px-2 w-[5%] ${index !== pagedRejected.length - 1 ? "border-b border-[#111C2D1A]" : ""}`} />
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
              <Pagination currentPage={rejectedPage} totalPages={rejectedTotalPages} onPageChange={setRejectedPage} resultCount={pagedRejected.length} />
            </>
          )}
        </div>
      </div>

      {/* Approve Confirmation Modal */}
      {isApproveConfirmOpen && (
        <div className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h2 className="text-xl font-semibold text-[#1F1F1F] mb-3">Are you sure?</h2>
            <p className="text-sm text-[#646464] mb-6 leading-relaxed">
              Once approved, it will be available for nutritionists to use and assign to clients.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="secondary"
                size="md"
                onClick={() => setIsApproveConfirmOpen(false)}
                disabled={isSubmittingApprove}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleConfirmApprove}
                disabled={isSubmittingApprove}
              >
                {isSubmittingApprove ? "Approving..." : "Approve"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Template Modal */}
      {isRejectModalOpen && (
        <div className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6">
            <h2 className="text-xl font-semibold text-[#1F1F1F] mb-5">Reject template</h2>
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#1F1F1F] mb-2">
                What's missing or needs improvement?
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Provide specific feedback or suggestions for changes."
                rows={5}
                className="w-full px-3 py-2.5 border border-[#E0E0E0] rounded-lg text-sm text-[#1F1F1F] placeholder:text-[#9E9E9E] focus:outline-none focus:border-[#0DB5B5] resize-none"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button
                variant="secondary"
                size="md"
                onClick={() => { setIsRejectModalOpen(false); setRejectReason(""); }}
                disabled={isSubmittingReject}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                size="md"
                onClick={handleSubmitReject}
                disabled={!rejectReason.trim() || isSubmittingReject}
              >
                {isSubmittingReject ? "Rejecting..." : "Reject"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer toasts={toast.toasts} onClose={toast.removeToast} />
    </div>
  );
};

export default GlobalTemplatePage;
